import {FC} from 'react';

import {Box, Skeleton, Stack} from '@mui/material';
import {t} from 'i18next';
import {Else, If, Then, When} from 'react-if';

// eslint-disable-next-line import/no-cycle
import {config} from 'config/env';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import SendBrowserEvent from 'services/send-browser-event';
import {
  AlcoTypeMapper,
  ProductSectionTypes,
  BasketLineItem,
  BasketSection,
  BasketItemType,
  ExpressTimeFrame
} from 'types';
import {MarketplaceShopType} from 'types';
import {ProductUnavailableReasonCodes} from 'types/recommendations';
import {
  findBasketConsignmentItems,
  findBasketDropshipItemsByShopName,
  getShopNamesFromBasketItems,
  hasItemsInBasket,
  getBulkyProducts,
  getPreOrderProducts,
  findBasketConsignmentItemsWithoutPreOrder,
  getAlcoholProducts,
  findUnavailableAlcoProducts,
  findUnavailableProductsWithoutAlco,
  get1PxBulkyProducts,
  basketDeliveryBy,
  basketDeliveryDateFormat,
  getProductShopName,
  isTimeEligibleForExpress,
  fetchLatestBasketItem
} from 'utils';
import {getPreOrderDeliverySlot} from 'utils';
import {BasketEmptyBody} from 'views/basket-empty-body/BasketEmptyBody';
import {ProductSection} from 'views/basket-product-section/ProductSection';
import {UnavailableSection} from 'views/basket-product-section/UnavailableSection';

import {COMPANY_NAME} from '../../constants';

export type ProductListProps = {
  loading?: boolean;
};

type GetSectionsResult = {
  sections: BasketSection[];
  flexiBasketSections?: any;
};
export const ProductList: FC<ProductListProps> = ({loading}) => {
  const {basket} = useAppSelector((state) => state?.basket);
  const {isExpress} = useAppMode();
  const UnavailableAlcoHeaderMapper: Record<string, string> = {
    [ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD]: t('alcohol.title.alcoholSalesProhibited'),
    [ProductUnavailableReasonCodes.ALCOHOL_LESS_THAN_REQUIRED_QUANTITY]: '',
    [ProductUnavailableReasonCodes.EXPIRED_ALCOHOL_LICENSE]: t('alcohol.title.renewYouLicense')
  };

  const isFlexibleBasketEnabled = config.IS_FLEXIBLE_BASKET_ENABLED;
  const basketStoreInfo = basket?.storeInfo?.[0];

  const unavailableProducts = basket?.unavailableItems || [];
  const availableProducts = basket?.basketItems || [];

  const unavailableProductsWitoutAlco = findUnavailableProductsWithoutAlco(unavailableProducts);
  const unavailableAlcoProducts = findUnavailableAlcoProducts(unavailableProducts);

  const getDropShipSection = (sectionIndex: number, marketplaceType: string, lineItems: BasketLineItem[]) => {
    const shippingInfo = lineItems?.length
      ? lineItems[0].productDetails?.shippingInfo?.length
        ? lineItems[0].productDetails?.shippingInfo[0]
        : undefined
      : undefined;

    const shopName = lineItems?.length ? lineItems[0].productDetails?.sellerDetails.shopName || '-' : '-';

    const titleV2 = shopName;

    const description = basketDeliveryDateFormat(
      shippingInfo?.deliveryTimeEarliestDate,
      shippingInfo?.deliveryTimeLatestDate
    );

    const section: BasketSection = {
      header: undefined,
      title: titleV2,
      description,
      data: lineItems,
      index: sectionIndex,
      marketplaceType: marketplaceType
    };
    return section;
  };

  const getSections = (): GetSectionsResult => {
    const flexiBasketSections: any = {};
    const sections: BasketSection[] = [];

    if (unavailableProductsWitoutAlco?.length) {
      const outOfStockSection: BasketSection = {
        header: t('label.notReadyToPurchase'),
        title: '',
        description: '',
        data: unavailableProductsWitoutAlco,
        index: sections.length,
        type: ProductSectionTypes.OUT_OF_STOCK_PRODUCTS
      };
      if (isFlexibleBasketEnabled) {
        flexiBasketSections.unavailableSection = outOfStockSection;
      } else {
        sections.push(outOfStockSection);
      }
    }

    if (unavailableAlcoProducts.length) {
      const alcoSection: BasketSection = {
        title: '',
        description: '',
        header:
          UnavailableAlcoHeaderMapper[
            unavailableAlcoProducts[0].productDetails?.unavailableReason?.code as ProductUnavailableReasonCodes
          ],
        data: unavailableAlcoProducts,
        index: sections.length,
        type: AlcoTypeMapper[
          unavailableAlcoProducts[0].productDetails?.unavailableReason?.code as ProductUnavailableReasonCodes
        ]
      };
      sections.push(alcoSection);
    }

    if (isExpress && availableProducts.length) {
      const shopName = getProductShopName(availableProducts[0].productDetails);
      const consignmentSection: BasketSection = {
        header: unavailableProducts?.length ? t('label.readyToPurchase') : undefined,
        title: t('message.deliveredBy', {
          shopName: shopName === 'Maknet' ? COMPANY_NAME : shopName || COMPANY_NAME
        }),
        description:
          basketStoreInfo && isTimeEligibleForExpress(basketStoreInfo.expressDeliveryTimeFrame as ExpressTimeFrame)
            ? t('express.title.expressDelivery')
            : t('express.title.outOfOrderingTime'),
        data: availableProducts,
        index: 1,
        type: ProductSectionTypes.EXPRESS_PRODUCTS,
        marketplaceType: MarketplaceShopType.CONSIGNMENT
      };
      sections.push(consignmentSection);
      return {sections, flexiBasketSections};
    }

    const consignmentItems = findBasketConsignmentItems(availableProducts, true);

    const consignmentItemsWithoutPreOrder = findBasketConsignmentItemsWithoutPreOrder(availableProducts);

    if (consignmentItems?.length) {
      const shopName = getProductShopName(consignmentItems[0].productDetails);
      const consignmentSection: BasketSection = {
        header: unavailableProducts?.length && !isFlexibleBasketEnabled ? t('label.readyToPurchase') : undefined,
        title: t('message.deliveredBy', {
          shopName: shopName === 'Maknet' ? COMPANY_NAME : shopName || COMPANY_NAME
        }),
        description: t('message.chooseDeliveryDate'),
        data: consignmentItems,
        index: 1,
        type: ProductSectionTypes.AVAILABLE_PRODUCTS,
        marketplaceType: MarketplaceShopType.CONSIGNMENT
      };
      sections.push(consignmentSection);
    }

    const alcoProducts = getAlcoholProducts(availableProducts);
    if (alcoProducts && alcoProducts.length) {
      const consignmentSection: BasketSection = {
        header: undefined,
        title: t('alcohol.title.alcoProducts'),
        description: t('alcohol.label.readT&C'),
        data: alcoProducts,
        index: sections?.length,
        type: ProductSectionTypes.ALCOHOL_PRODUCTS,
        marketplaceType: ProductSectionTypes.ALCOHOL_PRODUCTS,
        onlyAlcoItems: !consignmentItems.length
      };
      sections.push(consignmentSection);
    }

    const bulkyProducts = getBulkyProducts(availableProducts, true);
    const onePxBulkyProducts = get1PxBulkyProducts(availableProducts);

    if (bulkyProducts && bulkyProducts.length) {
      //const deliverySlot = getBulkyDeliverySlot();
      //const deliveryBy = basketDeliveryBy(deliverySlot.deliveryTimeEarliestDate, deliverySlot.deliveryTimeLatestDate);
      const consignmentSection: BasketSection = {
        header: undefined,
        title: t('sellerDelivery.bulky.largeAppliances'),
        description: t('label.chooseDeliverySlot'),
        data: bulkyProducts,
        index: sections?.length,
        type: ProductSectionTypes.AVAILABLE_PRODUCTS,
        marketplaceType:
          !consignmentItems.length && !alcoProducts.length && !onePxBulkyProducts.length
            ? MarketplaceShopType.CONSIGNMENT
            : ProductSectionTypes.BULKY_PRODUCTS,
        onlyBulkyItems: !consignmentItems.length && !alcoProducts.length && !onePxBulkyProducts.length,
        onlyBulkyAnd1PxBulkyItems: !consignmentItems.length && !alcoProducts.length
      };
      sections.push(consignmentSection);
    }

    if (onePxBulkyProducts?.length) {
      const consignmentSection: BasketSection = {
        header: undefined,
        title: t('sellerDelivery.bulky.largeAppliances'),
        description: t('label.chooseDeliverySlot'),
        data: onePxBulkyProducts,
        index: sections?.length,
        type: ProductSectionTypes.AVAILABLE_PRODUCTS,
        marketplaceType:
          !consignmentItems.length && !alcoProducts.length && !bulkyProducts.length
            ? MarketplaceShopType.CONSIGNMENT
            : BasketItemType.ONEPX,
        only1PxBulkyItems:
          !alcoProducts.length && !consignmentItems.length && !bulkyProducts.length && !!onePxBulkyProducts.length
      };
      sections.push(consignmentSection);
    }

    const preOrderProducts = getPreOrderProducts(availableProducts);
    if (preOrderProducts && preOrderProducts.length) {
      const deliverySlot = getPreOrderDeliverySlot();
      const deliveryBy = basketDeliveryBy(deliverySlot.deliveryTimeEarliestDate, deliverySlot.deliveryTimeLatestDate);
      const consignmentSection: BasketSection = {
        header: undefined,
        title: t('sellerDelivery.preOrder.preOrderProducts'),
        description: deliveryBy,
        data: preOrderProducts,
        index: sections?.length,
        type: ProductSectionTypes.AVAILABLE_PRODUCTS,
        marketplaceType:
          !consignmentItemsWithoutPreOrder.length && !alcoProducts.length
            ? MarketplaceShopType.CONSIGNMENT
            : ProductSectionTypes.PRE_ORDER_PRODUCTS,
        onlyPreOrderItems: !consignmentItemsWithoutPreOrder.length && !alcoProducts.length
      };
      sections.push(consignmentSection);
    }

    const shops = getShopNamesFromBasketItems(availableProducts);
    if (shops?.length) {
      //Flag to set header 'Available items' only for first dropship seller section
      let isAvailbleHeaderSet = false;
      shops.forEach(({name: shopName, id: shopId}) => {
        const itemsByShop = findBasketDropshipItemsByShopName(availableProducts, shopName);
        if (itemsByShop?.length) {
          const dropshipSection = getDropShipSection(sections?.length, shopName, itemsByShop);
          const sellerPrices = basket?.prices?.priceBreakdown.find((shop) => shop.shopName === shopName);
          dropshipSection.marketplaceType = sellerPrices?.marketplaceType;
          if (basket?.shippingFeeApplicablePromotions?.length) {
            dropshipSection.shippingFeePromotionText = basket?.shippingFeeApplicablePromotions.find(
              (promo) => promo.shopId === shopId
            )?.promotions?.[0]?.text;
          }
          if (unavailableProducts?.length && !consignmentItems?.length && !isAvailbleHeaderSet) {
            dropshipSection.header = !isFlexibleBasketEnabled ? t('label.readyToPurchase') : undefined;
            dropshipSection.type = ProductSectionTypes.AVAILABLE_PRODUCTS;
            //Update flag value to 'true' to avoid adding 'Available items' header for multiple dropship seller sections
            isAvailbleHeaderSet = true;
          }
          sections.push(dropshipSection);
        }
      });
    }
    let productSections: BasketSection[];
    const latestItem = fetchLatestBasketItem(basket?.basketItems as BasketLineItem[]);

    if (latestItem) {
      productSections = sections.map((section) => {
        const {data} = section;
        const matchIndex = data?.findIndex((item) => item.productDetails?.id === latestItem.productDetails?.id);
        if (matchIndex === -1) return section;

        const matchedItem = data.splice(matchIndex, 1)[0];
        data.unshift(matchedItem);

        return section;
      });
    } else {
      productSections = sections;
    }

    return {sections: productSections, flexiBasketSections};
  };

  const {sections, flexiBasketSections} = getSections();
  const onClickBrowseCategories = () => {
    SendBrowserEvent.goToHomePage();
  };
  return (
    <>
      <If condition={loading}>
        <Then>
          <Box>
            <Stack spacing={1} height={200} padding="30px 24px 30px 24px">
              <Skeleton variant="rectangular" height="100%" sx={{borderRadius: 2}} />
            </Stack>
          </Box>
        </Then>
        <Else>
          <If condition={hasItemsInBasket(basket)}>
            <Then>
              {sections.map((sec, index, sections) => (
                <ProductSection
                  key={`${sec.title}-${sec.index}-${index}`}
                  mode={`${sections.length > 1 ? 'multiple' : 'single'}`}
                  canUseBasketV5={true}
                  {...sec}
                />
              ))}
              <When condition={isFlexibleBasketEnabled && flexiBasketSections?.unavailableSection?.data?.length}>
                <UnavailableSection mode={'multiple'} {...flexiBasketSections?.unavailableSection} />
              </When>
            </Then>
            <Else>
              <BasketEmptyBody onClick={onClickBrowseCategories} isFlexibleBasketEnabled={isFlexibleBasketEnabled} />
            </Else>
          </If>
        </Else>
      </If>
    </>
  );
};

ProductList.displayName = 'ProductList';
